'use client';

import { cn } from '@ps/ui/utils';
import type { RenderElementProps } from 'slate-react';
import { useFocused, useSelected } from 'slate-react';

import type { MentionElement } from '../types/SlateNodes';

/**
 * Slate render component for inline `mention` chips inside the
 * editor. Visually matches `SlateMessageRenderer`'s mention chip
 * (mint-green token pill); adds an outline when the mention is
 * selected, mirroring PopshopRN's behavior so users see the chip
 * is targetable for backspace removal.
 *
 * The chip is `contentEditable={false}` so Slate treats its
 * children (the void text marker) as un-editable, which keeps the
 * mention atomic.
 */
export function SlateEditorMention({
  attributes,
  children,
  element,
}: RenderElementProps) {
  // Cast through `unknown` — Slate's Element type is augmented
  // elsewhere in the workspace; the renderer is only invoked when
  // `element.type === 'mention'`.
  const mention = element as unknown as MentionElement;
  const selected = useSelected();
  const focused = useFocused();
  const displayName = nicknameWithInitial(mention.profile);

  return (
    <span
      {...attributes}
      contentEditable={false}
      data-mention-user-id={mention.userId}
      className={cn(
        'bg-brand/10 text-brand-text mx-0.5 inline-block rounded px-1 py-px align-baseline text-[0.9em]',
        selected && focused ? 'outline-mint-green-300 outline outline-1' : '',
      )}
    >
      <span contentEditable={false}>
        {children}@{displayName}
      </span>
    </span>
  );
}

function nicknameWithInitial(profile: MentionElement['profile']): string {
  if (profile.storeFrontName) return profile.storeFrontName;
  const first = (profile.firstName ?? '').trim();
  const last = (profile.lastName ?? '').trim();
  if (last) return `${first} ${last.charAt(0).toUpperCase()}.`;
  return first || profile.username || 'mention';
}
